import React, { Component } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import { tableBookingWebSocketService } from '@/services/tableBookingWebSocket';
import styles from './MyWaitlist.module.scss';

interface WaitlistEntry {
  id: string;
  branchId: string;
  tableId?: string | null;
  status: string; // waiting | notified | converted | cancelled
  requestedDate: string;
  requestedStartTime?: string | null;
  requestedEndTime?: string | null;
  partySize: number;
  position: number;
  notes?: string | null;
  branch?: { name?: string; address?: string; organizerId?: string } | null;
  table?: { tableNumber?: number | string } | null;
}

interface State {
  entries: WaitlistEntry[];
  isLoading: boolean;
  error: string | null;
  toast: string | null;
}

function withNavigation(Wrapped: any) {
  return function WithNav(props: any) {
    const navigate = useNavigate();
    return <Wrapped {...props} navigate={navigate} />;
  };
}

class MyWaitlist extends Component<any, State> {
  private toastTimer: any = null;

  state: State = { entries: [], isLoading: true, error: null, toast: null };

  componentDidMount() {
    this.load();
    tableBookingWebSocketService.connect();
    tableBookingWebSocketService.on('booking_waitlist:notified', this.handleNotified);
  }

  componentWillUnmount() {
    tableBookingWebSocketService.off('booking_waitlist:notified', this.handleNotified);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  }

  handleNotified = () => {
    this.showToast('A table you waitlisted for is now available!');
    this.load();
  };

  showToast = (msg: string) => {
    this.setState({ toast: msg });
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => this.setState({ toast: null }), 6000);
  };

  load = async () => {
    this.setState({ isLoading: true, error: null });
    try {
      const res = await apiService.getMyWaitlist();
      if (res.success && res.data?.entries) {
        this.setState({ entries: res.data.entries as WaitlistEntry[], isLoading: false });
      } else {
        this.setState({ entries: [], isLoading: false, error: res.message || 'Failed to load waitlist' });
      }
    } catch (err) {
      this.setState({ isLoading: false, error: err instanceof Error ? err.message : 'Failed to load waitlist' });
    }
  };

  leave = async (id: string) => {
    if (!window.confirm('Leave this waitlist?')) return;
    try {
      const res = await apiService.leaveWaitlist(id);
      if (res.success) {
        this.showToast('Removed from waitlist.');
        this.load();
      } else {
        this.showToast(res.message || 'Could not leave the waitlist.');
      }
    } catch (err) {
      this.showToast(err instanceof Error ? err.message : 'Could not leave the waitlist.');
    }
  };

  formatDate = (d: string) => {
    const date = new Date(d);
    return isNaN(date.getTime()) ? d : date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  render() {
    const { entries, isLoading, error, toast } = this.state;

    return (
      <>
        <DashboardHeader />
        {toast && <div className={styles.toast}>{toast}</div>}
        <div className={styles.container}>
          <div className={styles.content}>
            <div className={styles.header}>
              <h1 className={styles.title}>My Waitlist</h1>
              <p className={styles.subtitle}>Venues you're waiting on — we'll notify you when a table frees up.</p>
            </div>

            {isLoading ? (
              <div className={styles.state}><div className={styles.spinner} /><p>Loading…</p></div>
            ) : error ? (
              <div className={styles.state}><div className={styles.icon}>⚠️</div><p>{error}</p><button className={styles.primaryBtn} onClick={this.load}>Retry</button></div>
            ) : entries.length === 0 ? (
              <div className={styles.state}>
                <div className={styles.icon}>🔔</div>
                <h3>You're not on any waitlists</h3>
                <p>When a venue is full, tap "Join waitlist" on its tables page to get notified.</p>
                <button className={styles.primaryBtn} onClick={() => this.props.navigate('/customer/organizers')}>Browse Organizers</button>
              </div>
            ) : (
              <div className={styles.list}>
                {entries.map((e) => (
                  <div key={e.id} className={`${styles.card} ${e.status === 'notified' ? styles.cardNotified : ''}`}>
                    <div className={styles.cardInfo}>
                      <div className={styles.cardTop}>
                        <span className={styles.venue}>{e.branch?.name || 'Venue'}</span>
                        {e.status === 'notified' ? (
                          <span className={styles.badgeReady}>Table available!</span>
                        ) : (
                          <span className={styles.badgeWaiting}>#{e.position} in line</span>
                        )}
                      </div>
                      <div className={styles.meta}>
                        <span>📅 {this.formatDate(e.requestedDate)}</span>
                        {e.requestedStartTime && <span>⏰ {String(e.requestedStartTime).slice(0, 5)}</span>}
                        <span>👥 {e.partySize}</span>
                        {e.table?.tableNumber != null && <span>Table {e.table.tableNumber}</span>}
                      </div>
                      {e.notes && <p className={styles.notes}>“{e.notes}”</p>}
                    </div>
                    <div className={styles.cardActions}>
                      {e.status === 'notified' && (
                        <button
                          className={styles.primaryBtn}
                          onClick={() =>
                            this.props.navigate(
                              e.branch?.organizerId
                                ? `/customer/organizers/${encodeURIComponent(e.branch.organizerId)}/branches/${encodeURIComponent(e.branchId)}/tables`
                                : '/customer/organizers'
                            )
                          }
                          title="Book now"
                        >
                          Book now
                        </button>
                      )}
                      <button className={styles.leaveBtn} onClick={() => this.leave(e.id)}>Leave</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <Footer />
      </>
    );
  }
}

export default withNavigation(MyWaitlist);
